import { useRef } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { useStore } from "@/store/useStore";

const lerp = THREE.MathUtils.lerp;

/* A small procedural "smart home" diorama that reacts to the Lotus Ring gestures:
   TV screen powers on/off, the door smart-lock bolts/unbolts, and a floor lamp
   responds to the flashlight. All original geometry. */
export default function SmartRoom() {
  const tv = useStore((s) => s.tv);
  const lock = useStore((s) => s.lock);
  const flashlight = useStore((s) => s.flashlight);

  const screen = useRef();
  const bars = useRef([]);
  const tvGlow = useRef();
  const bolt = useRef();
  const lockLed = useRef();
  const lampLight = useRef();
  const lampShade = useRef();
  const group = useRef();

  useFrame((state, dt) => {
    const k = Math.min(1, dt * 6);
    const t = state.clock.elapsedTime;

    // entrance pop
    if (group.current) group.current.scale.setScalar(lerp(group.current.scale.x, 1, k));

    // TV screen
    if (screen.current) {
      const m = screen.current.material;
      m.emissiveIntensity = lerp(m.emissiveIntensity, tv ? 1.4 : 0.03, k);
      m.color.lerp(new THREE.Color(tv ? "#0a2230" : "#0b0e15"), k);
    }
    bars.current.forEach((b, i) => {
      if (!b) return;
      const h = tv ? 0.1 + Math.abs(Math.sin(t * 3 + i)) * 0.9 : 0.02;
      b.scale.y = lerp(b.scale.y, h, 0.3);
      b.material.opacity = lerp(b.material.opacity, tv ? 0.9 : 0, 0.2);
    });
    if (tvGlow.current) tvGlow.current.intensity = lerp(tvGlow.current.intensity, tv ? 2.2 : 0, k);

    // smart lock
    if (bolt.current) bolt.current.position.x = lerp(bolt.current.position.x, lock ? 0.0 : -0.14, k);
    if (lockLed.current) {
      const col = lock ? "#f43f5e" : "#22c55e";
      lockLed.current.material.color.lerp(new THREE.Color(col), k);
      lockLed.current.material.emissive.lerp(new THREE.Color(col), k);
      lockLed.current.material.emissiveIntensity = 1.1 + Math.sin(t * 4) * 0.3;
    }

    // floor lamp (follows flashlight)
    if (lampLight.current) lampLight.current.intensity = lerp(lampLight.current.intensity, flashlight ? 3.2 : 0, k);
    if (lampShade.current) lampShade.current.material.emissiveIntensity = lerp(lampShade.current.material.emissiveIntensity, flashlight ? 1.3 : 0.05, k);
  });

  const wall = "#141924";
  const floor = "#0e121b";

  return (
    <group ref={group} position={[-2.1, -1.0, -0.6]} rotation={[0, 0.5, 0]} scale={0.001}>
      {/* floor */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0, 0]} receiveShadow>
        <planeGeometry args={[3.4, 3.4]} />
        <meshStandardMaterial color={floor} roughness={0.9} />
      </mesh>
      {/* back wall */}
      <mesh position={[0, 1.2, -1.7]}>
        <boxGeometry args={[3.4, 2.4, 0.08]} />
        <meshStandardMaterial color={wall} roughness={0.85} />
      </mesh>
      {/* left wall */}
      <mesh position={[-1.7, 1.2, 0]}>
        <boxGeometry args={[0.08, 2.4, 3.4]} />
        <meshStandardMaterial color={wall} roughness={0.85} />
      </mesh>
      {/* rug */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0.2, 0.01, 0.5]}>
        <planeGeometry args={[1.8, 1.4]} />
        <meshStandardMaterial color="#1b2333" roughness={1} />
      </mesh>

      {/* ---- TV on back wall ---- */}
      <group position={[0.1, 1.45, -1.63]}>
        <mesh>
          <boxGeometry args={[1.7, 1.0, 0.07]} />
          <meshStandardMaterial color="#05070c" metalness={0.5} roughness={0.4} />
        </mesh>
        <mesh ref={screen} position={[0, 0, 0.05]}>
          <planeGeometry args={[1.55, 0.86]} />
          <meshStandardMaterial color="#0b0e15" emissive="#00f0ff" emissiveIntensity={0.03} toneMapped={false} />
        </mesh>
        {/* animated content bars */}
        {[-0.6, -0.36, -0.12, 0.12, 0.36, 0.6].map((x, i) => (
          <mesh key={i} ref={(el) => (bars.current[i] = el)} position={[x, 0, 0.07]}>
            <planeGeometry args={[0.16, 0.7]} />
            <meshBasicMaterial color={i % 2 ? "#00f0ff" : "#a855f7"} transparent opacity={0} toneMapped={false} />
          </mesh>
        ))}
        {/* TV stand */}
        <mesh position={[0, -0.62, 0.02]}>
          <boxGeometry args={[0.1, 0.24, 0.1]} />
          <meshStandardMaterial color="#05070c" />
        </mesh>
      </group>
      <pointLight ref={tvGlow} position={[0.1, 1.4, -1.2]} color="#38e8ff" intensity={0} distance={5} />

      {/* ---- Door + smart lock on left wall ---- */}
      <group position={[-1.63, 0.95, 0.4]} rotation={[0, Math.PI / 2, 0]}>
        <mesh>
          <boxGeometry args={[0.9, 1.9, 0.08]} />
          <meshStandardMaterial color="#20283a" roughness={0.7} />
        </mesh>
        {/* lock plate */}
        <mesh position={[0.3, 0.0, 0.07]}>
          <boxGeometry args={[0.2, 0.34, 0.05]} />
          <meshStandardMaterial color="#0b0f18" metalness={0.6} roughness={0.4} />
        </mesh>
        {/* sliding bolt */}
        <mesh ref={bolt} position={[-0.14, 0.0, 0.08]}>
          <boxGeometry args={[0.24, 0.07, 0.07]} />
          <meshStandardMaterial color="#c9cdd6" metalness={0.9} roughness={0.3} />
        </mesh>
        {/* status LED */}
        <mesh ref={lockLed} position={[0.3, 0.12, 0.1]}>
          <circleGeometry args={[0.035, 20]} />
          <meshStandardMaterial color="#22c55e" emissive="#22c55e" emissiveIntensity={1} toneMapped={false} />
        </mesh>
      </group>

      {/* ---- Floor lamp (flashlight) ---- */}
      <group position={[1.4, 0, 0.6]}>
        <mesh position={[0, 0.02, 0]}>
          <cylinderGeometry args={[0.16, 0.2, 0.05, 20]} />
          <meshStandardMaterial color="#05070c" metalness={0.5} roughness={0.5} />
        </mesh>
        <mesh position={[0, 0.7, 0]}>
          <cylinderGeometry args={[0.025, 0.025, 1.4, 12]} />
          <meshStandardMaterial color="#1b2230" metalness={0.6} roughness={0.4} />
        </mesh>
        <mesh ref={lampShade} position={[0, 1.5, 0]}>
          <coneGeometry args={[0.28, 0.32, 24, 1, true]} />
          <meshStandardMaterial color="#efe4c4" emissive="#ffd27a" emissiveIntensity={0.05} side={THREE.DoubleSide} />
        </mesh>
        <pointLight ref={lampLight} position={[0, 1.35, 0]} color="#ffcf87" intensity={0} distance={6} decay={1.4} />
      </group>

      {/* simple sofa for context */}
      <group position={[0.3, 0.26, 0.7]}>
        <mesh>
          <boxGeometry args={[1.5, 0.4, 0.7]} />
          <meshStandardMaterial color="#232c3e" roughness={0.9} />
        </mesh>
        <mesh position={[0, 0.3, -0.3]}>
          <boxGeometry args={[1.5, 0.5, 0.18]} />
          <meshStandardMaterial color="#2a3448" roughness={0.9} />
        </mesh>
      </group>
    </group>
  );
}
